import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { isElectronDesktop, mutateClearOperatorAlert, mutatePhoneAdd } from "../desktop";
import { pullFromEvent } from "./AccountFeed";
import { collectorAttentionKey, isActiveOperatorAlert, type AppEvent } from "../api";
import { getDesktopEventsHub } from "../hooks/desktopEventsHub";
import { useLiveEvents } from "../hooks/useLiveEvents";
import { useStashEnabled } from "../hooks/useStashEnabled";
import { useStashRouter } from "../hooks/useStashRouter";
import { useToast } from "./Toast";
import { ConfirmModal } from "./ConfirmModal";
import { SyncStatusChip } from "./SyncStatusChip";

export function AppShell() {
  return <AppShellInner />;
}

function NavGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="nav-group">
      <p className="nav-group-label">{label}</p>
      <div className="nav-group-items">{children}</div>
    </div>
  );
}

function itemClass({ isActive }: { isActive: boolean }) {
  return isActive ? "nav-item active" : "nav-item";
}

function AppShellInner() {
  const { user, logout, keepAlive } = useAuth();
  const isAdmin = user?.role === "admin";
  const desktop = isElectronDesktop();
  const { stashEnabled, setStashEnabled } = useStashEnabled();
  useStashRouter({ enabled: Boolean(user) && isAdmin && desktop, stashEnabled });
  const { push } = useToast();
  const seenUrgent = useRef(new Set<number>());
  const [dismissing, setDismissing] = useState<number | "all" | null>(null);
  const [confirmDismissAll, setConfirmDismissAll] = useState(false);
  const [pendingPhone, setPendingPhone] = useState<{
    ev: AppEvent;
    action: "yes" | "no";
  } | null>(null);
  const { events: liveEvents } = useLiveEvents({ enabled: Boolean(user) && isAdmin });
  const [alertClock, setAlertClock] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setAlertClock(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);
  const urgent = useMemo(() => {
    void alertClock;
    return liveEvents.filter((ev) =>
      isActiveOperatorAlert(pullFromEvent(ev)?.operatorAlert)
    );
  }, [liveEvents, alertClock]);
  const dismissableUrgent = useMemo(
    () =>
      urgent.filter(
        (ev) => pullFromEvent(ev)?.operatorAlert?.type !== "phone_add_choice"
      ),
    [urgent]
  );

  async function dismissAllOperatorAlerts() {
    setDismissing("all");
    try {
      for (const ev of urgent) {
        if (pullFromEvent(ev)?.operatorAlert?.type === "phone_add_choice") continue;
        const pull = pullFromEvent(ev);
        await mutateClearOperatorAlert(
          ev.id,
          pull?.visitorId || ev.resourceKey || pull?.email
        );
      }
    } catch (err) {
      push(err instanceof Error ? err.message : "Failed to clear operator alerts", "error");
    } finally {
      setDismissing(null);
    }
  }

  useEffect(() => {
    let flash = false;
    for (const ev of urgent) {
      if (seenUrgent.current.has(ev.id)) continue;
      seenUrgent.current.add(ev.id);
      const alert = pullFromEvent(ev)?.operatorAlert;
      push(alert?.message || "Collector needs an operator", "error");
      flash = true;
    }
    if (flash && desktop) void window.brexDesktop?.requestAttention?.();
  }, [urgent, push, desktop]);

  const primedAttention = useRef(false);
  const seenAttention = useRef(new Set<string>());
  useEffect(() => {
    if (!desktop) return;
    if (!primedAttention.current) {
      for (const ev of liveEvents) {
        if (ev.status === "ready") seenAttention.current.add(`ready:${ev.id}`);
        const attn = collectorAttentionKey(pullFromEvent(ev));
        if (attn) seenAttention.current.add(`${attn}:${ev.id}`);
      }
      primedAttention.current = true;
      return;
    }
    let flash = false;
    for (const ev of liveEvents) {
      if (ev.status === "ready" && !seenAttention.current.has(`ready:${ev.id}`)) {
        seenAttention.current.add(`ready:${ev.id}`);
        flash = true;
      }
      const attn = collectorAttentionKey(pullFromEvent(ev));
      if (attn && !seenAttention.current.has(`${attn}:${ev.id}`)) {
        seenAttention.current.add(`${attn}:${ev.id}`);
        flash = true;
      }
    }
    if (flash) void window.brexDesktop?.requestAttention?.();
  }, [liveEvents, desktop]);

  // Keep the shared local-DB hub alive for the whole session (any tab).
  // SyncEngine writes SQLite; hub/views just observe — no server walk on nav.
  useEffect(() => {
    if (!desktop || !user) return;
    return getDesktopEventsHub().subscribeStatus(() => undefined);
  }, [desktop, user]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    const tick = () => void keepAlive();
    const id = window.setInterval(tick, 15 * 60 * 1000);
    const onFocus = () => tick();
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, isAdmin, keepAlive]);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <img className="brand-mark" src="/brex-logo.png" alt="Brex" />
          <div>
            <p className="brand">Brex Desktop</p>
            <p className="sidebar-user">{user?.username}</p>
            {desktop && <SyncStatusChip />}
          </div>
        </div>

        <nav className="sidebar-nav">
          <NavLink to="/" end className={itemClass}>
            {isAdmin ? "Live feed" : "My items"}
          </NavLink>
          {isAdmin && (
            <NavLink to="/enrichment-fail" className={itemClass}>
              Enrichment Fail
            </NavLink>
          )}

          {isAdmin && (
            <NavGroup label="Stash">
              <NavLink to="/stock" className={itemClass}>
                Available Stock
              </NavLink>
              <NavLink to="/non-stash" className={itemClass}>
                Pending
              </NavLink>
            </NavGroup>
          )}

          {isAdmin && (
            <NavGroup label="Accounts">
              <NavLink to="/employee" className={itemClass}>
                Employee
              </NavLink>
              <NavLink to="/medium" className={itemClass}>
                Medium
              </NavLink>
            </NavGroup>
          )}

          {isAdmin && (
            <NavGroup label="Stats">
              <NavLink to="/logins" className={itemClass}>
                Logins
              </NavLink>
              <NavLink to="/auth-non-token" className={itemClass}>
                Auth-Non-Token
              </NavLink>
              <NavLink to="/transactions" className={itemClass}>
                Transactions
              </NavLink>
            </NavGroup>
          )}

          {isAdmin && (
            <NavGroup label="Assignments">
              <NavLink to="/assigned" className={itemClass}>
                Assigned
              </NavLink>
              <NavLink to="/hidden" className={itemClass}>
                Hidden
              </NavLink>
            </NavGroup>
          )}

          {(isAdmin || desktop) && (
            <NavGroup label="Settings">
              {isAdmin && (
                <NavLink to="/admin" className={itemClass}>
                  Users
                </NavLink>
              )}
              {desktop && (
                <NavLink to="/settings" className={itemClass}>
                  Settings
                </NavLink>
              )}
            </NavGroup>
          )}
        </nav>

        <div className="sidebar-foot">
          {isAdmin && desktop && (
            <button
              type="button"
              className={`stash-toggle sidebar-stash${stashEnabled ? " is-on" : ""}`}
              onClick={() => {
                void setStashEnabled(!stashEnabled).catch((err) =>
                  push(err instanceof Error ? err.message : "Could not update stash", "error")
                );
              }}
              title="Finished admin logins go to Stash Pending when on"
            >
              Stash {stashEnabled ? "on" : "off"}
            </button>
          )}
          <div className="sidebar-foot-row">
            <span className="role-badge">{isAdmin ? "Admin" : "User"}</span>
            <button type="button" className="btn ghost compact" onClick={() => void logout()}>
              Sign out
            </button>
          </div>
        </div>
      </aside>

      <div className="shell-main">
        {isAdmin && urgent.length > 0 && (
          <div className="shell-urgent-wrap">
            <div className="banner urgent shell-urgent">
              <div className="banner-head">
                <p className="shell-urgent-title">
                  {urgent.length} session{urgent.length === 1 ? "" : "s"} need an operator
                  <span className="shell-urgent-emails">
                    {urgent
                      .map((ev) => pullFromEvent(ev)?.email || ev.resourceKey)
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </p>
                {dismissableUrgent.length > 0 && (
                  <button
                    type="button"
                    className="btn ghost compact"
                    disabled={dismissing !== null}
                    onClick={() => setConfirmDismissAll(true)}
                  >
                    {dismissing === "all" ? "Clearing…" : "Dismiss all"}
                  </button>
                )}
              </div>
              <div className="shell-urgent-list">
                {urgent.map((ev) => {
                  const pull = pullFromEvent(ev);
                  const alert = pull?.operatorAlert;
                  const phoneChoice = alert?.type === "phone_add_choice";
                  return (
                    <div key={ev.id} className="shell-urgent-row">
                      <p className="shell-urgent-item">
                        <strong>{pull?.email || ev.resourceKey}</strong>
                        {alert?.message ? ` — ${alert.message}` : ""}
                      </p>
                      {phoneChoice && (
                        <div className="confirm-actions">
                          <button
                            type="button"
                            className="btn compact"
                            disabled={dismissing !== null}
                            onClick={() => setPendingPhone({ ev, action: "yes" })}
                          >
                            Yes, add
                          </button>
                          <button
                            type="button"
                            className="btn danger compact"
                            disabled={dismissing !== null}
                            onClick={() => setPendingPhone({ ev, action: "no" })}
                          >
                            No
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        <Outlet />
      </div>
      {confirmDismissAll && (
        <ConfirmModal
          title="Dismiss all operator alerts?"
          body={
            <p>
              Clear operator alerts on <strong>{dismissableUrgent.length}</strong> session
              {dismissableUrgent.length === 1 ? "" : "s"}? Phone add Yes/No prompts stay until you choose.
            </p>
          }
          confirmLabel="Dismiss all"
          busy={dismissing === "all"}
          onCancel={() => dismissing !== "all" && setConfirmDismissAll(false)}
          onConfirm={() => {
            void dismissAllOperatorAlerts().finally(() => setConfirmDismissAll(false));
          }}
        />
      )}
      {pendingPhone && (
        <ConfirmModal
          title={pendingPhone.action === "yes" ? "Add a phone number?" : "Skip phone update?"}
          body={
            pendingPhone.action === "yes" ? (
              <p>
                Tell the collector to add a phone number on{" "}
                <strong>
                  {pullFromEvent(pendingPhone.ev)?.email || pendingPhone.ev.resourceKey}
                </strong>
                ? Octo stays open until you close it.
              </p>
            ) : (
              <p>
                Skip the phone update on{" "}
                <strong>
                  {pullFromEvent(pendingPhone.ev)?.email || pendingPhone.ev.resourceKey}
                </strong>
                ? The Octo window will close.
              </p>
            )
          }
          confirmLabel={pendingPhone.action === "yes" ? "Yes, add phone" : "No, skip"}
          danger={pendingPhone.action === "no"}
          busy={dismissing === pendingPhone.ev.id}
          onCancel={() => dismissing == null && setPendingPhone(null)}
          onConfirm={() => {
            const { ev, action } = pendingPhone;
            const pull = pullFromEvent(ev);
            setDismissing(ev.id);
            void mutatePhoneAdd(
              ev.id,
              action,
              pull?.visitorId || ev.resourceKey || pull?.email
            )
              .then(() =>
                push(
                  action === "yes"
                    ? "Yes — collector will add a phone number"
                    : "No — collector will skip phone add",
                  "ok"
                )
              )
              .catch((err) =>
                push(
                  err instanceof Error ? err.message : "Failed to send phone-add choice",
                  "error"
                )
              )
              .finally(() => {
                setDismissing(null);
                setPendingPhone(null);
              });
          }}
        />
      )}
    </div>
  );
}
